import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AIJobStatus, BrandPlanPreparation } from 'types'
import {
  useCreateBrandPlanPreparation,
  Props,
  Options,
  Variables,
} from 'hooks/queries/brandPlans/useCreateBrandPlanPreparation'

export const fetchPreparation = async ({
  brandPlanId,
  id,
  workspaceId,
  signal,
}: {
  id: number
  brandPlanId: number
  workspaceId: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<{ brand_plan_preparation: BrandPlanPreparation }>(
    `/w/${workspaceId}/brand_plans/${brandPlanId}/preparations/${id}`,
    { signal },
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useCreateBrandPlanPreparationSync = (props: Props, options?: Options) => {
  const { brandPlanId } = props
  const { mutateAsync: createPreparation } = useCreateBrandPlanPreparation(props)
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandPlanPreparation, Error, Variables>(async (vars) => {
    const { prompt, signal } = vars

    let preparation = await createPreparation({
      prompt,
      signal,
    })

    let interval = 500

    do {
      const result = await fetchPreparation({ id: preparation.id, workspaceId, brandPlanId })

      preparation = result.brand_plan_preparation

      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (preparation?.status === AIJobStatus.Failed) {
        throw new Error('Failed prepare brand plan')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(preparation?.status))

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans'])

    return preparation
  }, options)
}
